import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle } from 'lucide-react';
import { RaceForm, type RaceFormData } from '@/components/races/RaceForm';
import { RaceVisibility } from "@/gql/graphql.ts";
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { GET_RACE_FOR_EDIT } from '@/graphql/queries';
import { UPDATE_RACE } from '@/graphql/mutations';
import { useToast } from '@/components/ui/Toast';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

export function EditRacePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();
    const [isDirty, setIsDirty] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);

    const handleDirtyChange = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
    }, []);

    // Warn on browser close/refresh
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const handleBack = () => {
        const backPath = `/races/${id}`;
        if (isDirty) {
            setPendingNavPath(backPath);
            setShowLeaveModal(true);
        } else {
            navigate(backPath);
        }
    };

    const confirmLeave = () => {
        setShowLeaveModal(false);
        if (pendingNavPath) navigate(pendingNavPath);
    };

    const { data, loading: isLoadingQuery, error } = useQuery(GET_RACE_FOR_EDIT, {
        variables: { id: id! },
        skip: !id,
        fetchPolicy: 'network-only'
    });

    const [updateRace, { loading: isSaving }] = useMutation(UPDATE_RACE);
    const { toast } = useToast();

    const handleUpdate = async (formData: RaceFormData) => {
        if (!id) return;

        try {
            await updateRace({
                variables: {
                    id,
                    input: {
                        name: formData.name,
                        description: formData.description,
                        raceFormat: formData.raceFormat,
                        distance: formData.distance,
                        elevationGain: formData.elevationGain,
                        bettingDeadline: formData.bettingDeadline,
                        visibility: formData.visibility,
                        totalRunners: formData.totalRunners,
                        tokenCount: formData.tokenCount,
                    }
                }
            });
            setIsDirty(false);
            navigate(`/races/${id}`, { replace: true });
        } catch (err) {
            console.error(err);
            toast(t('edit.error'), 'error');
        }
    };

    if (isLoadingQuery) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
        );
    }

    if (error || !data?.race) {
        return (
            <div className="p-8 space-y-4">
                <ErrorBanner message={t('edit.loadError')} />
                <div className="text-center">
                    <Link to="/races" className="text-emerald-600 font-bold underline">{tc('btn.back')}</Link>
                </div>
            </div>
        );
    }

    return (
        <Page>
            <PageHeader
                title={t('edit.title')}
                onBack={handleBack}
                width="narrow"
            />

            <PageContent width="narrow" className="pb-24">
                <RaceForm
                    initialValues={{
                        name: data.race.name || '',
                        description: data.race.description || '',
                        raceFormat: data.race.raceFormat || undefined,
                        distance: data.race.distance,
                        elevationGain: data.race.elevationGain,
                        bettingDeadline: data.race.bettingDeadline,
                        visibility: data.race.visibility || RaceVisibility.Public,
                        totalRunners: data.race.totalRunners,
                        tokenCount: data.race.bettingConfig?.tokenCount,
                    }}
                    onSubmit={handleUpdate}
                    isLoading={isSaving}
                    submitLabel={t('edit.submit')}
                    tokenCountLocked={(data.race.bettorCount ?? 0) > 0}
                    lockedFields={[
                        ...(data.race.isUtmbRace ? ['raceFormat', 'distance', 'elevationGain'] as const : []),
                        ...(data.race.isOfficial ? ['visibility'] as const : []),
                    ]}
                    lockedFieldMessages={data.race.isOfficial ? { visibility: t('details.officialCannotBePrivate') } : {}}
                    onDirtyChange={handleDirtyChange}
                />
            </PageContent>

            {showLeaveModal && (
                <ConfirmModal
                    icon={<AlertTriangle size={24} />}
                    iconClassName="bg-amber-50 text-amber-600"
                    title={t('edit.unsavedTitle')}
                    message={t('edit.unsavedMessage')}
                    confirmLabel={t('edit.unsavedConfirm')}
                    onConfirm={confirmLeave}
                    onCancel={() => setShowLeaveModal(false)}
                />
            )}
        </Page>
    );
}
